import React from 'react';

import { PERMISSION_ACTIONS } from '~/components/core/Permissions/PermissionUtils';
import GenericTemplatesConfigurationScreen from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplatesConfigurationScreen';
import CatEventsTable from '~/components/SystemConfiguration/CatastropheEvents';
import StatReserveConfigurationPage from '~/components/SystemConfiguration/ClaimConfiguration/StatReserveConfiguration/StatReserveConfigurationPage';
import CliveConfiguration from '~/components/SystemConfiguration/CliveConfiguration';
import ConfigurationManagement from '~/components/SystemConfiguration/ConfigurationManagement/ConfigurationManagement';
import ContactRolesConfiguration from '~/components/SystemConfiguration/ContactRoles';
import DataImport from '~/components/SystemConfiguration/DataImport/DataImport';
import DelegatedAuthorityTab from '~/components/SystemConfiguration/DelegatedAuthority/DelegatedAuthorityTab';
import DocumentTypesConfigurationScreen from '~/components/SystemConfiguration/DocumentTypes/DocumentTypesConfigurationScreen';
import GlobalConfiguration from '~/components/SystemConfiguration/GlobalConfiguration';
import InternalAuditLog from '~/components/SystemConfiguration/InternalAuditLog/InternalAuditLog';
import NotesConfiguration from '~/components/SystemConfiguration/NotesConfiguration';
import SystemOfRecord from '~/components/SystemConfiguration/SystemOfRecord';
import { DemoConfigurationTabInternal } from '~/components/SystemConfiguration/Tabs/DemoConfiguration/DemoConfigurationTab';
import type { OrganizationModel } from '~/components/types/organization-types';
import { ADMIN_ROUTES } from '~/routesConstants';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import AutomaticRulesConfiguration from '../AutomaticRulesConfiguration';
import ClaimConfigurationPage from '../ClaimConfiguration/ClaimConfiguration';
import CommunicationConfigurations from '../Communications/CommunicationConfigurations';
import MetabaseConfigurationPage from '../Communications/MetabaseConfigurationTab/MetabaseConfigurationTab';
import DamageAssessmentTypesScreen from '../DamageAssessmentTypes/DamageAssessmentTypesScreen';
import ScheduledDataExportPage from '../DataExport/ScheduledDataExport';
import { ExposureConfigurationToolBackendWrapper } from '../ExposureConfiguration/ExposureConfigurationToolBackendWrapper';
import FeaturesTab from '../FeaturesTab';
import { InternalFnolConfiguration } from '../FnolConfiguration';
import IncidentConfiguration from '../IncidentConfiguration/IncidentConfiguration';
import IntegrationTab from '../Integrations/InternalAdmin';
import InvoiceConfigurationScreen from '../InvoiceConfiguration/InvoiceConfigurationScreen';
import ITConfigurationTab from '../ITConfigurationTab';
import LoginConfiguration from '../LoginConfiguration';
import { MoiConfigurationToolBackendWrapper } from '../MoiConfig/MoiConfigurationToolBackendWrapper';
import NotificationsConfiguration from '../NotificationsConfiguration';
import OperationsTab from '../OperationsTab';
import OrganizationTab from '../OrganizationTab';
import { InternalPolicyConfiguration } from '../PolicyConfiguration';
import TableauAccounts from '../TableauAccounts';
import { AdjusterManagementTab } from '../Tabs/AdjusterManagement/AdjusterManagementTab';
import DeveloperToolsTab from '../Tabs/DeveloperTools/DeveloperToolsTab';
import PaymentsConfigurationTab from '../Tabs/PaymentsConfiguration/PaymentsConfigurationTab';
import { PermissionsProfilesPage } from '../Tabs/PermissionsProfiles/PermissionsProfilesPage';
import TriageAndReservesTab from '../TriageAndReservesTab';
import UsersManagementTab from '../Users/UsersManagementTab';
import Vendors from '../VendorsConfigurations/Vendors';

export interface SuperOrgTabDef {
  label: string;
  url: string;
  component: React.ComponentType;
  featureFlag?: string;
  isActive?: (organization: OrganizationModel) => boolean;
  /** Granular sysconfig scope gating this tab when granular sysconfig enforcement is effective. */
  permissionAction?: string;
}

export const superOrgTabs: SuperOrgTabDef[] = [
  { label: 'IT & Security', url: ADMIN_ROUTES.IT_SECURITY, component: ITConfigurationTab },
  { label: 'Permission Profiles', url: ADMIN_ROUTES.PERMISSION_PROFILES, component: PermissionsProfilesPage },
  { label: 'Developer Tools', url: ADMIN_ROUTES.DEVELOPER_TOOLS.MAIN, component: DeveloperToolsTab },
  { label: 'Features', url: ADMIN_ROUTES.FEATURES, component: FeaturesTab as unknown as React.ComponentType },
  { label: 'Organization', url: ADMIN_ROUTES.ORGANIZATION.MAIN, component: OrganizationTab },
  { label: 'Payments Configuration', url: ADMIN_ROUTES.PAYMENTS_CONFIGURATION, component: PaymentsConfigurationTab },
  { label: 'Triage & Reserves', url: ADMIN_ROUTES.TRIAGE_RESERVES, component: TriageAndReservesTab },
  { label: 'Integrations', url: ADMIN_ROUTES.INTEGRATIONS, component: IntegrationTab },
  { label: 'Vendors', url: ADMIN_ROUTES.VENDORS, component: Vendors },
  { label: 'Communications', url: ADMIN_ROUTES.COMMUNICATIONS, component: CommunicationConfigurations },
  {
    label: 'Automation Rules Configuration',
    url: ADMIN_ROUTES.AUTOMATION.MAIN,
    component: AutomaticRulesConfiguration,
  },
  { label: 'Login Configuration', url: ADMIN_ROUTES.LOGIN_CONFIGURATION, component: LoginConfiguration },
  { label: 'MOI Configuration', url: ADMIN_ROUTES.MOI_CONFIG, component: MoiConfigurationToolBackendWrapper },
  {
    label: 'Coverage Configuration',
    url: ADMIN_ROUTES.EXPOSURE_CONFIG,
    component: ExposureConfigurationToolBackendWrapper,
  },
  {
    label: 'Damage Assessment Types',
    url: ADMIN_ROUTES.DAMAGE_ASSESSMENT_TYPES,
    component: DamageAssessmentTypesScreen,
    featureFlag: CONFIGURATION_FEATURES_NAMES.DAMAGE_ASSESSMENT_TYPE_CONFIG,
  },
  {
    label: 'Invoice Configuration',
    url: ADMIN_ROUTES.INVOICE_CONFIGURATION,
    component: InvoiceConfigurationScreen,
    featureFlag: CONFIGURATION_FEATURES_NAMES.DAMAGE_ASSESSMENT_TYPE_CONFIG,
  },
  { label: 'Incident Configuration', url: ADMIN_ROUTES.INCIDENT_CONFIG, component: IncidentConfiguration },
  { label: 'Claim Configuration', url: ADMIN_ROUTES.CLAIM_CONFIG.MAIN, component: ClaimConfigurationPage },
  {
    label: 'FNOL Configuration',
    url: ADMIN_ROUTES.FNOL_CONFIGURATION,
    component: InternalFnolConfiguration,
    featureFlag: CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL,
  },
  {
    label: 'Global Configuration',
    url: ADMIN_ROUTES.GLOBAL_CONFIGURATION.MAIN,
    component: GlobalConfiguration,
    featureFlag: CONFIGURATION_FEATURES_NAMES.GLOBAL_CONFIGURED_FIELDS,
  },
  {
    label: 'Policy Configuration',
    url: ADMIN_ROUTES.POLICY_CONFIGURATION,
    component: InternalPolicyConfiguration,
    featureFlag: CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL,
  },
  {
    label: 'Tableau Accounts Manager',
    url: ADMIN_ROUTES.TABLEAU_ACCOUNTS,
    component: TableauAccounts,
    isActive: (organization) => !isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED),
  },
  {
    label: 'Templates',
    url: ADMIN_ROUTES.GENERIC_TEMPLATES_CONFIGURATION,
    component: GenericTemplatesConfigurationScreen,
  },
  {
    label: 'Contact Roles',
    url: ADMIN_ROUTES.CONTACT_ROLES,
    component: ContactRolesConfiguration,
  },
  {
    label: 'Document Types',
    url: ADMIN_ROUTES.DOCUMENT_TYPES,
    component: DocumentTypesConfigurationScreen,
  },
  { label: 'Notes Configuration', url: ADMIN_ROUTES.NOTES_CONFIG, component: NotesConfiguration },
  {
    label: 'Catastrophic Events',
    url: ADMIN_ROUTES.ORGANIZATION.CATASTROPHIC_EVENTS,
    component: CatEventsTable,
  },
  {
    label: 'Reserve Configuration',
    url: ADMIN_ROUTES.STAT_RESERVES,
    component: StatReserveConfigurationPage,
  },
  {
    label: 'Clive Configuration',
    url: ADMIN_ROUTES.CLIVE_CONFIGURATION.MAIN,
    component: CliveConfiguration,
  },
  {
    label: 'Internal Data Export',
    url: 'Internal_data_export',
    component: ScheduledDataExportPage,
    featureFlag: CONFIGURATION_FEATURES_NAMES.SCHEDULED_DATA_EXPORT,
  },
  {
    label: 'Demo Configuration',
    url: ADMIN_ROUTES.DEMO_CONFIGURATION.MAIN,
    component: DemoConfigurationTabInternal,
    isActive: (organization: OrganizationModel) => organization.organization_type === 'demo',
  },
  {
    label: 'Notifications Configuration',
    url: ADMIN_ROUTES.NOTIFICATIONS_CONFIGURATION.MAIN,
    component: NotificationsConfiguration,
  },
  {
    // Always available internally (isInternal). Snapshots & Backup + Promotions show unconditionally
    // here; Audit Log + Change Requests still gate on STARR_DEMO_AUDIT_LOG inside the component.
    // The CONFIGURATION_MANAGEMENT flag only gates the external (customer) surface (OperationsTab).
    label: 'Configuration Management',
    url: 'configuration_management',
    component: () => <ConfigurationManagement isInternal />,
  },
  {
    label: 'Internal Audit Log',
    url: ADMIN_ROUTES.INTERNAL_AUDIT_LOG,
    component: InternalAuditLog,
  },
  {
    label: 'Metabase Config',
    url: 'metabase_config',
    component: MetabaseConfigurationPage,
  },
  {
    label: 'Delegated Authority',
    url: ADMIN_ROUTES.DELEGATED_AUTHORITY,
    component: DelegatedAuthorityTab,
    featureFlag: CONFIGURATION_FEATURES_NAMES.DELEGATED_AUTHORITY,
  },
  { label: 'Data Import', url: ADMIN_ROUTES.DATA_IMPORT, component: DataImport },
  {
    // The configuration is saved here only; the organization's own users read it, test it and work its Issues from
    // Operations.
    label: 'System of Record',
    url: 'system_of_record',
    component: () => <SystemOfRecord isInternal />,
    featureFlag: CONFIGURATION_FEATURES_NAMES.SYSTEM_OF_RECORD,
  },
];

export const adminCustomerFacingTabs: SuperOrgTabDef[] = [
  {
    label: 'User Management',
    url: 'user_management',
    component: UsersManagementTab,
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_USER_MANAGEMENT,
  },
  {
    label: 'Adjuster Management',
    url: 'adjuster_management',
    component: AdjusterManagementTab,
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_ADJUSTER_MANAGEMENT,
  },
  { label: 'Operations', url: 'operations', component: OperationsTab },
];
